import { ProductAlertsActionsCellRenderer } from '@features/reference/products/components/ProductAlertsActionsCellRenderer';
import { ProductContractsActionsCellRenderer } from '@features/reference/products/components/ProductContractsActionsCellRenderer';
import { getAlertLevelStyles } from '@shared/styles/alertStyles';
import { formatDatetime } from '@utils/date';
import { ColDef } from 'ag-grid-enterprise';
import { Alert } from './alerts';
import { PricingType } from './pricing';

type Settlement = {
  source: string;
  code: string;
  desc?: string;
};

export type FlatProduct = {
  contract_size: number;
  min_size: number;
  max_size: number;
  barrels_per_metric_ton: number;
  barrels_per_mmbtu: number | null;
  holiday_calendar: string;
  option_strike: number;
  option_type: FlatProductOptionType;
  settlement: FlatProductSettlement;
};

type FlatProductOptionType = 'unspecified' | 'call' | 'put';
type FlatProductSettlement = { source: string; code: string; desc: string };
export type SettlementTimeKey = 'unspecified' | 'utc_1630' | 'utc_0830';

export type ExchangeSymbol = {
  exchange: string;
  symbol: string;
  cqg_symbol: string;
  tt_symbol: string;
  clearing_venue: string;
  is_balmo: boolean;
  name: string;
  contract_size: number;
  min_size: number;
  max_size: number;
  holiday_calendar: string;
  cqg_tas_symbol: string;
  contract_type: string;
  quantity_unit: string;
  market_close: SettlementTimeKey;
};

export type ProductWeight = {
  product: Product;
  weight: number;
};

export type Contract = {
  product_symbol: string;
  symbol: string;
  expiry: string;
  first_settlement: string;
  last_settlement: string;
  id: number;
  product_id: number;
  tenor_code: string;
};

export type Composition = {
  weight: number;
  product: Product;
};

type DefaultExchange = 'cme' | 'ice' | 'unspecified' | 'onyx';

export enum ProductType {
  UNSPECIFIED = 'unspecified',
  SPOT = 'spot',
  FUTURE = 'future',
  SWAP = 'swap',
  WEEKLY_SWAP = 'weekly_swap',
  DAILY_SWAP = 'daily_swap',
  FUTURE_SWAP = 'future_swap',
  OPTION = 'option',
  COMPOSITE = 'composite',
  CALENDAR = 'calendar',
  INDEX = 'index',
  MOC = 'moc',
}

export type Product = {
  symbol: string;
  full_name: string;
  short_name: string;
  id: number;
  product_type: ProductType;
  product_group: string;
  calendar_type: CalendarType;
  mdx_name: string;
  currency: string;
  multiplier: number;
  currency_unit: string;
  underlying_symbol: string;
  exchange_symbols: ExchangeSymbol[];
  settlement?: Settlement;
  specification: FlatProduct | null;
  tenor_frequency: string;
  quantity_unit: string;
  pricing_tenors: number;
  composition: Composition[];
  contracts: Contract[];
  tick_size: number;
  enabled: boolean;
  private: boolean;
  tradable: boolean;
  default_exchange: DefaultExchange | null;
  pricing_type: PricingType;
};

export type TenorFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export const TenorFrequencyValues = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
} as const;

export const enum CalendarType {
  UNSPECIFIED = 'unspecified',
  FLAT = 'flat',
  SPREAD = 'spread',
  QUARTER = 'quarter',
  BUTTERFLY = 'butterfly',
  CONDOR = 'condor',
  YEAR = 'year',
}

export type ProductTenor = {
  code: string;
  display: string;
  frequency: TenorFrequency;
  spread_code?: string;
  tenor_date?: string;
};

export type ProductMetaData = {
  region: string;
  description: string;
  exchange_symbol: string;
  abn_exchange_code_trade: string;
  abn_opposite_party: string;
  abn_external_member: string;
  abn_external_account: string;
  eagle_exchange_code: string;
  product_symbol: string;
  exchange: string;
  is_balmo: boolean;
  id: number;
};

export const productMaps = (products: Product[], tenors: ProductTenor[]) => {
  const productMap = products.reduce((acc, product) => {
    acc[product.symbol] = product;
    return acc;
  }, {});

  const tenorMap = tenors.reduce((acc, tenor) => {
    acc[tenor.code] = tenor;
    if (tenor.spread_code) acc[tenor.spread_code] = tenor;
    return acc;
  }, {});

  return {
    productMap,
    tenorMap,
  };
};

export const PRODUCT_COMPOSITIONS_COLUMNS: ColDef<Composition>[] = [
  {
    field: 'weight',
    headerName: 'Weight',
    resizable: true,
    headerTooltip: 'Weight',
  },
  {
    colId: 'symbol',
    field: 'product.symbol',
    headerName: 'Symbol',
    headerTooltip: 'Symbol',
    flex: 1,
  },
  {
    field: 'product.full_name',
    headerName: 'Name',
    resizable: true,
    flex: 1,
    headerTooltip: 'Product Full Name',
  },
];

export const PRODUCT_CONTRACTS_COLUMNS: ColDef<Contract>[] = [
  {
    field: 'symbol',
    headerName: 'Symbol',
    headerTooltip: 'Symbol',
    width: 110,
  },
  {
    field: 'expiry',
    headerName: 'Expiry',
    resizable: true,
    headerTooltip: 'Product Symbol Expiry',
    valueFormatter: ({ value }) => (value ? formatDatetime(value) : ''),
    flex: 1,
  },
  {
    headerName: 'Actions',
    cellRenderer: ProductContractsActionsCellRenderer,
    pinned: 'right',
  },
];

export const PRODUCT_EXCHANGE_SYMBOLS_COLUMNS: ColDef<ExchangeSymbol>[] = [
  {
    field: 'exchange',
    headerName: 'Exchange',
    headerTooltip: 'Exchange',
    width: 90,
  },
  {
    field: 'symbol',
    headerName: 'Symbol',
    headerTooltip: 'Symbol',
    width: 90,
  },
  {
    field: 'cqg_symbol',
    headerName: 'CQG Symbol',
    headerTooltip: 'CQG Symbol',
    width: 90,
  },
  {
    field: 'tt_symbol',
    headerName: 'TT Symbol',
    headerTooltip: 'TT Symbol',
    width: 90,
  },
  {
    field: 'clearing_venue',
    headerName: 'Clearing Venue',
    headerTooltip: 'Clearing Venue',
    width: 110,
  },
  {
    field: 'is_balmo',
    headerName: 'Is Balmo',
    headerTooltip: 'Is Balmo',
    cellDataType: 'text',
    width: 90,
  },
  {
    field: 'name',
    headerName: 'Name',
    headerTooltip: 'Name',
    width: 90,
  },
  {
    field: 'contract_size',
    headerName: 'Contract Size',
    headerTooltip: 'Contract Size',
    width: 90,
  },
  {
    field: 'min_size',
    headerName: 'Min Size',
    headerTooltip: 'Min Size',
    width: 90,
  },
  {
    field: 'max_size',
    headerName: 'Max size',
    headerTooltip: 'Max size',
    width: 90,
  },
  {
    field: 'holiday_calendar',
    headerName: 'Holiday Calendar',
    headerTooltip: 'Holiday Calendar',
    width: 120,
  },
  {
    field: 'cqg_tas_symbol',
    headerName: 'CQG TAS Symbol',
    headerTooltip: 'CQG TAS Symbol',
    width: 110,
  },
  {
    field: 'contract_type',
    headerName: 'Contract Type',
    headerTooltip: 'Contract Type',
    width: 100,
  },
  {
    field: 'quantity_unit',
    headerName: 'Quantity Unit',
    headerTooltip: 'Quantity Unit',
    width: 100,
  },
  {
    field: 'market_close',
    headerName: 'Market Close',
    headerTooltip: 'Market Close',
    width: 100,
    valueFormatter: ({ value }) =>
      value && value !== 'unspecified'
        ? `${SETTLEMENT_TIME_MAP[value as SettlementTimeKey].time} (${SETTLEMENT_TIME_MAP[value as SettlementTimeKey].timezone})`
        : '',
  },
];

export const PRODUCT_ALERTS_COLUMNS: ColDef<Alert>[] = [
  {
    field: 'group',
    headerName: 'Group',
    headerTooltip: 'Group',
    width: 90,
  },
  {
    field: 'counter',
    headerName: 'Counter',
    headerTooltip: 'Counter',
    width: 90,
  },
  {
    field: 'level',
    headerName: 'Level',
    headerTooltip: 'Level',
    width: 90,
    cellStyle: ({ value }) => {
      const { color } = getAlertLevelStyles(value);
      return { backgroundColor: color };
    },
  },
  {
    field: 'message',
    headerName: 'Message',
    headerTooltip: 'Message',
    flex: 1,
  },
  {
    field: 'timestamp',
    headerName: 'Timestamp',
    headerTooltip: 'Timestamp',
    valueFormatter: ({ value }) => (value ? formatDatetime(value) : ''),
    width: 150,
  },
  {
    headerName: 'Actions',
    cellRenderer: ProductAlertsActionsCellRenderer,
    cellStyle: { display: 'flex', justifyContent: 'center' },
    pinned: 'right',
  },
];

export const PRODUCT_RELATED_COLUMNS: ColDef<Product>[] = [
  {
    field: 'symbol',
    headerName: 'Symbol',
    headerTooltip: 'Symbol',
    width: 90,
  },
  {
    field: 'product_group',
    headerName: 'Product Group',
    headerTooltip: 'Product Group',
    width: 90,
  },
  {
    field: 'product_type',
    headerName: 'Product Type',
    headerTooltip: 'Product Type',
    width: 90,
  },
  {
    field: 'full_name',
    headerName: 'Product Name',
    headerTooltip: 'Full porduct Name',
    flex: 1,
  },
];

export const PRODUCT_META_DATA_COLUMNS: ColDef<ProductMetaData>[] = [
  {
    field: 'region',
    headerName: 'Region',
    headerTooltip: 'Region',
    width: 90,
  },
  {
    field: 'description',
    headerName: 'Description',
    headerTooltip: 'Description',
    width: 90,
  },
  {
    field: 'exchange_symbol',
    headerName: 'Exchange Symbol',
    headerTooltip: 'Exchange Symbol',
    width: 90,
  },
  {
    field: 'abn_exchange_code_trade',
    headerName: 'ABN Exchange Code Trade',
    headerTooltip: 'ABN Exchange Code Trade',
    width: 90,
  },
  {
    field: 'abn_opposite_party',
    headerName: 'ABN Opposite Party',
    headerTooltip: 'ABN Opposite Party',
    width: 90,
  },
  {
    field: 'abn_external_member',
    headerName: 'ABN External Member',
    headerTooltip: 'ABN External Member',
    width: 90,
  },
  {
    field: 'abn_external_account',
    headerName: 'ABN External Account',
    headerTooltip: 'ABN External Account',
    width: 90,
  },
  {
    field: 'eagle_exchange_code',
    headerName: 'Eagle Exchange Code',
    headerTooltip: 'Eagle Exchange Code',
    width: 90,
  },
  {
    field: 'product_symbol',
    headerName: 'Product Symbol',
    headerTooltip: 'Product Symbol',
    width: 90,
  },
  {
    field: 'exchange',
    headerName: 'Exchange',
    headerTooltip: 'Exchange',
    width: 90,
  },
  {
    field: 'is_balmo',
    headerName: 'Is Balmo',
    headerTooltip: 'Is Balmo',
    width: 90,
  },
  {
    field: 'id',
    headerName: 'ID',
    headerTooltip: 'ID',
    width: 90,
  },
];

export const SETTLEMENT_TIME_MAP: Record<SettlementTimeKey, { time: string; timezone: string }> = {
  unspecified: { time: 'unspecified', timezone: 'UTC' },
  utc_1630: { time: '16:30', timezone: 'UTC' },
  utc_0830: { time: '08:30', timezone: 'UTC' },
} as const;
